/**
 * Converts an amount from one currency to another using a `rate_to_usd` map.
 *
 * Each entry in `rates` represents how many USD one unit of that currency equals
 * (e.g., EUR: 1.08 means 1 EUR = 1.08 USD).
 *
 * @example
 * // 50000 EUR → USD at EUR rate 1.08:
 * // 50000 * 1.08 / 1.0 = 54000
 * convertCurrency(50000, 'EUR', 'USD', { USD: 1.0, EUR: 1.08 }) // 54000
 *
 * @returns The converted amount, or NaN if:
 *   - `amount` is non-finite
 *   - `rates[fromCurrency]` is missing, 0, or non-finite
 *   - `rates[toCurrency]` is missing, 0, or non-finite
 */
export function convertCurrency(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  rates: Record<string, number>
): number {
  if (!isFinite(amount)) {
    return NaN;
  }

  if (fromCurrency === toCurrency) {
    return amount;
  }

  const fromRate = rates[fromCurrency];
  const toRate = rates[toCurrency];

  if (fromRate == null || !isFinite(fromRate) || fromRate === 0) {
    return NaN;
  }
  if (toRate == null || !isFinite(toRate) || toRate === 0) {
    return NaN;
  }

  return (amount * fromRate) / toRate;
}

/**
 * Converts and sums an array of multi-currency amounts into a single target currency.
 *
 * @returns The summed result, or undefined if the array is empty/undefined or any
 *   individual conversion fails (e.g. missing rate).
 */
export function convertSumAmounts(
  arr: { amount: number; unit: string }[] | undefined,
  targetCurrency: string,
  rates: Record<string, number>
): { amount: number; unit: string } | undefined {
  if (!arr || arr.length === 0) {
    return undefined;
  }
  let total = 0;
  for (const { amount, unit } of arr) {
    const converted = convertCurrency(amount, unit, targetCurrency, rates);
    if (!Number.isFinite(converted)) {
      return undefined;
    }
    total += converted;
  }
  return { amount: total, unit: targetCurrency };
}
